import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBilling } from "../billing/hooks/use-billing";

const CANCEL_REASONS = [
  "settings.cancelSubscription.reasons.tooExpensive",
  "settings.cancelSubscription.reasons.notUsing",
  "settings.cancelSubscription.reasons.foundAlternative",
  "settings.cancelSubscription.reasons.technicalIssues",
  "settings.cancelSubscription.reasons.other",
];

export const CancelSubscriptionDialog: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string>("");

  // Billing hook - handles all state and actions
  const {
    billingData,
    isCancelDialogOpen,
    closeCancelDialog,
    handleCancelSubscription: onCancelSubscription,
    handleKeepPlan: onKeepPlan,
    isCancellingSubscription,
  } = useBilling();

  const resetsAt = billingData?.data?.subscription?.resetsAt;
  const resetDate = resetsAt
    ? new Date(resetsAt).toLocaleDateString(i18n.language, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const handleCancelSubscription = () => {
    if (selectedReason) {
      onCancelSubscription(t(selectedReason));
      setSelectedReason("");
    }
  };

  const handleClose = () => {
    setSelectedReason("");
    closeCancelDialog();
  };

  if (!isCancelDialogOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 " onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-white dark:bg-gray-800 rounded-[20px] shadow-xl max-w-lg w-full mx-4 max-h-[80vh] md:max-h-[90vh] overflow-y-auto mt-16 md:mt-0 ">
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-zinc-800 dark:text-white">
              {t("settings.cancelSubscription.title")}
            </h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-zinc-800 dark:text-white" />
            </button>
          </div>

          {/* Description */}
          <p className="text-sm text-zinc-500 dark:text-gray-300">
            {t("settings.cancelSubscription.description", { date: resetDate })}
          </p>

          {/* You will lose section */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-700 dark:text-gray-300">
              {t("settings.cancelSubscription.youWillLose")}
            </h3>
            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg space-y-4">
              <div className="flex items-center gap-2.5">
                <XIcon className="text-red-500 w-4 h-4" />
                <span className="text-sm text-zinc-700 dark:text-gray-300">
                  {t("settings.cancelSubscription.interactiveChat")}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <XIcon className="text-red-500 w-4 h-4" />
                <span className="text-sm text-zinc-700 dark:text-gray-300">
                  {t("settings.cancelSubscription.inputTokens")}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <XIcon className="text-red-500 w-4 h-4" />
                <span className="text-sm text-zinc-700 dark:text-gray-300">
                  {t("settings.cancelSubscription.advancedModels")}
                </span>
              </div>
            </div>
          </div>

          {/* Reason selection */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-zinc-700 dark:text-gray-300">
              {t("settings.cancelSubscription.whyCancel")}
            </label>
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-[10px] h-10 flex items-center justify-between px-3">
              <select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="w-full bg-transparent text-sm text-zinc-500 dark:text-gray-400 focus:outline-none"
              >
                <option value="">
                  {t("settings.cancelSubscription.selectReason")}
                </option>
                {CANCEL_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {t(reason)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onKeepPlan}
              className="px-3.5 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-border-primary dark:border-border-primary text-text-secondary dark:text-text-secondary text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("settings.cancelSubscription.keepPlan")}
            </button>
            <Button
              onClick={handleCancelSubscription}
              disabled={!selectedReason || isCancellingSubscription}
              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-[10px]"
            >
              {isCancellingSubscription
                ? t("common.loading") + "..."
                : t("settings.cancelSubscription.cancelSubscription")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
